// Enseñarle a Noa, DESDE EL CHAT.
//
// El cuaderno ya apuntaba lo que no sabía contestar, pero saberlo no servía de
// nada si enseñárselo exigía tocar el código y desplegar. Esto cierra el
// círculo en el sitio donde aparece el hueco: la pregunta que acaba de fallar
// viene rellenada, se escribe la respuesta y la siguiente persona que pregunte
// algo parecido ya la recibe.
//
// 🔴 Solo con la capacidad de Sistemas. Lo que se escriba aquí lo dirá Noa a
// toda la dirección como si fuera suyo — no puede escribirlo cualquiera. El
// botón se esconde por educación; quien manda es el servidor, que lo vuelve a
// comprobar.
import React, { useEffect, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, TextInput, View } from 'react-native';
import { api, type NoaEnsenanza } from '@/api/client';
import { C, F, R } from '@/constants/theme';
import { BottomSheet } from './ui';

/* Los valores que el servidor sabe rellenar. La lista de verdad la manda él
   (`/noa/valores`); esta es la misma con su explicación en castellano, porque
   un código suelto no le dice nada a nadie. */
const VALORES: Array<{ k: string; q: string }> = [
  { k: 'nombre', q: 'su nombre de pila' },
  { k: 'nombre_completo', q: 'nombre y apellido' },
  { k: 'rol', q: 'su rol en Pulse' },
  { k: 'hoteles', q: 'cuántos hoteles ve' },
  { k: 'email', q: 'su correo' },
  { k: 'fecha', q: 'hoy' },
  { k: 'hora', q: 'ahora' },
];

export default function EnsenarNoa({ visible, pregunta, onClose, modo = 'ensenar', onGuardado }: {
  visible: boolean; pregunta: string; onClose: () => void;
  modo?: 'ensenar' | 'repaso';
  onGuardado?: () => void;
}) {
  const [texto, setTexto] = useState('');
  const [pie, setPie] = useState('');
  const [preg, setPreg] = useState(pregunta);
  const [estado, setEstado] = useState<'edita' | 'guardando' | 'hecho'>('edita');
  const [error, setError] = useState<string | null>(null);
  const [lista, setLista] = useState<NoaEnsenanza[] | null>(null);
  const [repaso, setRepaso] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setPreg(pregunta); setTexto(''); setPie(''); setEstado('edita'); setError(null);
    setRepaso(modo === 'repaso');
    if (modo === 'repaso') { setLista(null); api.noaEnsenanzas().then((r) => setLista(r.ok ? r.dato.lista : [])); }
  }, [visible, pregunta, modo]);

  const guardar = async () => {
    setEstado('guardando'); setError(null);
    const r = await api.noaEnsena(preg.trim(), texto.trim(), pie.trim() || undefined);
    if (r.ok) { setEstado('hecho'); onGuardado?.(); setTimeout(onClose, 1100); }
    else { setEstado('edita'); setError(r.motivo); }
  };

  const verRepaso = async () => {
    setRepaso(true);
    const r = await api.noaEnsenanzas();
    setLista(r.ok ? r.dato.lista : []);
  };

  const olvidar = async (id: string) => {
    await api.noaOlvida(id);
    setLista((l) => (l ?? []).filter((x) => x.id !== id));
  };

  return (
    <BottomSheet visible={visible} onClose={onClose} maxAlto="90%">
      <ScrollView showsVerticalScrollIndicator={false} bounces={false} overScrollMode="never" keyboardShouldPersistTaps="handled">
        {!repaso ? (
          <>
            <Text style={s.h}>Enseñarle a Noa</Text>
            <Text style={s.sub}>
              Lo que escribas aquí se lo dirá a quien pregunte algo parecido. Va después de
              todo lo que calcula, así que nunca tapará una cifra del día — y solo contesta
              si la pregunta se parece bastante: antes callar que responder lo que no toca.
            </Text>

            <Text style={s.lbl}>LA PREGUNTA</Text>
            <TextInput
              style={[s.in, { minHeight: 46 }]} value={preg} onChangeText={setPreg}
              placeholder="¿Cuál es el teléfono de mantenimiento de…?" placeholderTextColor={C.faint} multiline
            />
            <Text style={s.pista}>
              Escríbela como la diría alguien. No hace falta que coincida palabra por palabra:
              Noa la reconoce si se parece bastante.
            </Text>

            <Text style={s.lbl}>LA RESPUESTA</Text>
            <TextInput
              style={[s.in, { minHeight: 110, textAlignVertical: 'top' }]} value={texto} onChangeText={setTexto}
              placeholder="Lo que quieres que conteste, tal cual." placeholderTextColor={C.faint} multiline
            />
            {/* La respuesta a «¿cómo me llamo?» no puede ser un texto fijo:
                depende de quién pregunta. Estos huecos se rellenan al contestar,
                con los datos de esa persona. Se listan aquí porque si no, hay
                que saberlos de memoria — y entonces nadie los usa. */}
            <Text style={s.pista}>Puedes meter estos huecos y se rellenan solos:</Text>
            <View style={s.chips}>
              {VALORES.map((v) => (
                <Pressable key={v.k} style={s.chip} onPress={() => setTexto((t) => `${t}{${v.k}}`)}>
                  <Text style={s.chipK}>{`{${v.k}}`}</Text>
                  <Text style={s.chipQ}>{v.q}</Text>
                </Pressable>
              ))}
            </View>
            <Text style={s.pista}>
              Ejemplo: «Te llamas {'{nombre}'}» → «Te llamas Patricio». Un hueco mal escrito se
              queda a la vista, para que se note.
            </Text>

            <Text style={s.lbl}>NOTA AL PIE  ·  opcional</Text>
            <TextInput
              style={s.in} value={pie} onChangeText={setPie}
              placeholder="De dónde sale, a quién preguntar…" placeholderTextColor={C.faint}
            />

            {error ? <Text style={s.err}>{error}</Text> : null}

            <Pressable
              style={[s.btn, (!texto.trim() || estado !== 'edita') && { opacity: 0.5 }]}
              disabled={!texto.trim() || estado !== 'edita'}
              onPress={guardar}
            >
              <Text style={s.btnTxt}>
                {estado === 'guardando' ? 'Guardando…' : estado === 'hecho' ? '✓ Aprendido' : 'Enseñárselo'}
              </Text>
            </Pressable>

            <Pressable style={s.link} onPress={verRepaso}>
              <Text style={s.linkTxt}>Ver lo que ya le hemos enseñado</Text>
            </Pressable>
          </>
        ) : (
          <>
            <Text style={s.h}>Lo que le hemos enseñado</Text>
            {lista == null ? <Text style={s.sub}>Cargando…</Text>
              : !lista.length ? <Text style={s.sub}>Todavía nada. Cuando Noa no sepa algo, enséñaselo desde su respuesta.</Text>
              : lista.map((x) => (
                <View key={x.id} style={s.fila}>
                  <View style={{ flex: 1 }}>
                    <Text style={s.filaP} numberOfLines={2}>{x.pregunta}</Text>
                    <Text style={s.filaR} numberOfLines={3}>{x.respuesta}</Text>
                    {x.quien ? <Text style={s.filaQ}>{x.quien}</Text> : null}
                  </View>
                  <Pressable hitSlop={10} onPress={() => olvidar(x.id)}>
                    <Text style={s.olvidar}>olvidar</Text>
                  </Pressable>
                </View>
              ))}
            <Pressable style={s.link} onPress={() => (modo === 'repaso' ? onClose() : setRepaso(false))}>
              <Text style={s.linkTxt}>{modo === 'repaso' ? 'Cerrar' : '← volver'}</Text>
            </Pressable>
          </>
        )}
      </ScrollView>
    </BottomSheet>
  );
}

const s = StyleSheet.create({
  h: { fontFamily: F.displayMed, fontSize: 20, color: C.ink, marginBottom: 4 },
  sub: { fontFamily: F.ui, fontSize: 12, color: C.muted, lineHeight: 17, marginBottom: 16 },
  lbl: { fontFamily: F.uiSemi, fontSize: 10.5, color: C.aquaSoft, letterSpacing: 0.7, marginBottom: 6, marginTop: 12 },
  in: {
    backgroundColor: C.card, borderColor: C.line, borderWidth: 1, borderRadius: 14,
    paddingHorizontal: 13, paddingVertical: 11, color: C.ink, fontFamily: F.ui, fontSize: 13, lineHeight: 19,
  },
  pista: { fontFamily: F.ui, fontSize: 10.5, color: C.faint, marginTop: 6, lineHeight: 15 },
  err: { fontFamily: F.uiSemi, fontSize: 12, color: C.bad, marginTop: 12 },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 7, marginTop: 8 },
  chip: { borderWidth: 1, borderColor: 'rgba(30,211,199,0.32)', backgroundColor: 'rgba(30,211,199,0.06)',
    borderRadius: 12, paddingHorizontal: 9, paddingVertical: 6 },
  chipK: { fontFamily: F.uiBold, fontSize: 11, color: C.aquaSoft },
  chipQ: { fontFamily: F.ui, fontSize: 9.5, color: C.faint, marginTop: 1 },
  btn: { backgroundColor: C.aqua, borderRadius: R.pill, paddingVertical: 14, alignItems: 'center', marginTop: 20 },
  btnTxt: { fontFamily: F.uiBold, fontSize: 14, color: '#04252A' },
  link: { paddingVertical: 14, alignItems: 'center' },
  linkTxt: { fontFamily: F.uiSemi, fontSize: 12.5, color: C.aquaSoft },
  fila: { flexDirection: 'row', gap: 12, alignItems: 'flex-start', paddingVertical: 12, borderBottomColor: C.line, borderBottomWidth: 1 },
  filaP: { fontFamily: F.uiSemi, fontSize: 12.5, color: C.ink },
  filaR: { fontFamily: F.ui, fontSize: 11.5, color: C.muted, marginTop: 3, lineHeight: 16 },
  filaQ: { fontFamily: F.ui, fontSize: 10, color: C.faint, marginTop: 4 },
  olvidar: { fontFamily: F.uiSemi, fontSize: 11.5, color: C.bad },
});
